import * as React from 'react';
import { Alert, ExpandableSection, Modal, Spinner, TextContent } from '@amzn/awsui-components-react-v3';

import { getGroupInfo, getWorkspace } from 'src/api/permissions';
import { createGroupDetailLink } from 'src/routes';
import { Link } from 'src/components/common/Link';
import { useEffect, useState } from 'react';
import { contactCTILink } from 'src/components/workspaces/common/common';
import { groupingMechanismIdUrl } from 'src/commons/common';
import {
  generateArnFromId,
  generateIdFromIdInfo,
  IdInfo,
  inferDataSourceFromIdInfo,
} from 'src/components/utils/arnUtil';
import { listMetadataEntitiesAssociatedToResource, listMetadataFields, listMetadataValues } from 'src/api/catalog';

export interface RequestContactInfoModalProps {
  request: any;
  visible: boolean;
  setVisible: any;
}

export const RequestContactInfoModal = (props: RequestContactInfoModalProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [groupId, setGroupId] = useState<string>(undefined);
  const [emailAddress, setEmailAddress] = useState<string>(undefined);
  const [groupingId, setGroupingId] = useState<string>(undefined);
  const [wikiLink, setWikiLink] = useState<string>(undefined);
  const [category, setCategory] = useState<string>(undefined);
  const [type, setType] = useState<string>(undefined);
  const [item, setItem] = useState<string>(undefined);
  const [oncall, setOncall] = useState<string>(undefined);
  const [notes, setNotes] = useState<string>(undefined);

  useEffect(() => {
    handleRefresh();
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    setError(false);
    try {
      const ownerId = props.request.owner;
      let localGroupId: string;
      if (ownerId?.startsWith('wks-')) {
        const getWorkspaceResponse = await getWorkspace({
          workspaceId: ownerId,
        });
        localGroupId = getWorkspaceResponse.workspace?.groupId;
      } else {
        localGroupId = ownerId;
      }
      setGroupId(localGroupId);

      const getGroupResponse = await getGroupInfo({
        groupId: localGroupId,
      });
      setEmailAddress(getGroupResponse.teamInfo?.DistributionList);
      setGroupingId(getGroupResponse.groupingMechanismId);
      setWikiLink(getGroupResponse.teamInfo?.Wiki);
      setCategory(getGroupResponse.primaryCTI?.category);
      setType(getGroupResponse.primaryCTI?.type);
      setItem(getGroupResponse.primaryCTI?.item);

      try {
        await fetchPrimaryContactDetailsIfPresent();
      } catch (metadataErr) {
        console.warn('Failed to fetch resource-level contact metadata; falling back to group-level info:', metadataErr);
      }
    } catch (err) {
      console.error('Error loading contact information:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrimaryContactDetailsIfPresent = async () => {
    let idInfo: IdInfo = {
      dataSource: undefined,
      catalogId: props.request.catalogId,
      clusterId: props.request.clusterId,
      redshiftWorkgroupName: props.request.redshiftWorkgroupName,
      bucketName: props.request.bucketName,
      schemaName: props.request.schemaName,
      databaseName: props.request.databaseName,
      tableName: props.request.tableName,
      region: props.request.region ?? 'us-east-1',
    };

    // Attempt to infer the data source ID. This is not guaranteed to be correct.
    // If it is incorrect, we won't be able to fetch the metadata, but we can still fall back to
    // group-level metadata.
    const dataSourceId = inferDataSourceFromIdInfo(idInfo);
    idInfo = {
      ...idInfo,
      dataSource: dataSourceId,
    };

    const resourceArn = generateArnFromId(generateIdFromIdInfo(idInfo));
    const listMetadataResponse = await listMetadataEntitiesAssociatedToResource({
      ResourceArn: resourceArn,
      MetadataEntityFilter: 'Form',
    });

    const formIds = listMetadataResponse.FormList.filter((form) => form.Name == 'Primary Contact Details').map(
      (form) => form.MetadataFormId,
    );
    if (!formIds || formIds.length == 0) {
      return;
    }
    const formId = formIds[0];

    const listFieldsResponse = await listMetadataFields({
      MetadataFormId: formId,
    });

    const listValuesResponse = await listMetadataValues({
      MetadataFormId: formId,
      ResourceArn: resourceArn,
    });

    const fieldsHavingValues = listFieldsResponse.MetadataFieldsList.filter(
      (field) => listValuesResponse.MetadataValuesList.filter((value) => value.Id == field.MetadataFieldId).length > 0,
    );

    fieldsHavingValues.forEach((field) => {
      const value: string = listValuesResponse.MetadataValuesList.find((val) => val.Id == field.MetadataFieldId).Value
        .StringValue;
      if (field.Name == 'Wiki') {
        setWikiLink(value);
      } else if (field.Name == 'Notes') {
        setNotes(value);
      } else if (field.Name == 'Oncall rotation') {
        setOncall(value);
      } else if (field.Name == 'Email') {
        setEmailAddress(value);
      } else if (field.Name == 'POSIX owner') {
        setGroupingId(value);
      } else if (field.Name == 'CTI') {
        const components = value.split('/');
        if (components.length == 3) {
          setCategory(components[0]);
          setType(components[1]);
          setItem(components[2]);
        }
      }
    });
  };

  const ownerGroupLink = () => {
    return <Link href={createGroupDetailLink(groupId)}>{groupId}</Link>;
  };

  const cti = () => {
    const url = contactCTILink(category, type, item, 'Request ' + props.request.requestId);
    const title = category + '/' + type + '/' + item;
    return (
      <Link external={true} href={url}>
        {title}
      </Link>
    );
  };

  const groupingLink = () => {
    const url = groupingMechanismIdUrl(groupingId);
    return (
      <Link external={true} href={url}>
        {groupingId}
      </Link>
    );
  };

  const oncallRotationLink = () => {
    return (
      <Link external={true} href={oncall}>
        {oncall}
      </Link>
    );
  };

  const modalContent = () => {
    if (loading) {
      return <Spinner />;
    }
    if (error) {
      return (
        <Alert type='error' header='Error'>
          Unable to load contact information. Please try again later.
        </Alert>
      );
    }
    return (
      <TextContent>
        The datasets in this request are owned by {ownerGroupLink()}. For support with your request, please reach out to
        them:
        <ul>
          {emailAddress && <li>Team email: {emailAddress}</li>}
          {groupingId && <li>POSIX/Teams: {groupingLink()}</li>}
          {wikiLink && (
            <li>
              Wiki:{' '}
              <Link external={true} href={wikiLink}>
                {wikiLink}
              </Link>
            </li>
          )}
          {category && type && item && <li>CTI: {cti()}</li>}
          {oncall && <li>Oncall rotation: {oncallRotationLink()}</li>}
        </ul>
        {notes && <p>Additional notes from the data provider: {notes}</p>}
      </TextContent>
    );
  };

  return (
    <Modal visible={props.visible} header={'Contact information'} onDismiss={() => props.setVisible(false)}>
      {modalContent()}
    </Modal>
  );
};
